/**
 * E07-F06-T04 — the Publish screen + success dialog.
 *
 * 1:1 port of the research prototype `../EER app/prototype/assets/research.proto.js`
 * (renderPublish / doPublish, lines 1166-1252). The screen shows:
 *   - counts: accepted total + Grade A/B/C (from GET /research/:id/publish);
 *   - an amber warning block when accepted rows are still needs_human;
 *   - a gate/auto explainer line (review_mode);
 *   - three independent checkboxes (Export to Sheets / Send to recruiter / Save to
 *     library) — Save-to-library is checked by DEFAULT;
 *   - a Publish button that POSTs the chosen options;
 *   - E07-F06-T08: a "Download .xlsx" button — the same EER-column table as a
 *     file (GET /research/:id/publish/xlsx), available without Google Sheets;
 *   - E07-F06-T10: a "Send the list to sourcers" block — pick one or more
 *     sourcers (Address Book, category Sourcer) as To; the CC is pre-filled from
 *     the Address Book category "Research Publish CC" (sourcing teamlead +
 *     Himanshu) and can be edited per publish.
 * On success a dialog shows a green check, the clickable + copyable Sheets link,
 * "Sent to <recruiter> · linked to <project>", and a "Back to library" button.
 * The dialog's outcome copy is driven by the SUBMITTED options, not by the raw
 * result counters — see `outcomeLines`.
 *
 * No `dangerouslySetInnerHTML` — every server value (sheet url / recruiter /
 * project) is rendered as React text; the link href is a plain `<a>` and the copy
 * uses the clipboard API on the raw string (XSS-safe).
 */
import { useState, type JSX } from 'react';
import {
  DEFAULT_PUBLISH_OPTIONS,
  type PublishOptions,
  type PublishRecipient,
  type PublishResult,
} from '@eer/shared';
import { Dialog } from '../ui/Dialog.js';
import { useToast } from '../ui/ToastContext.js';
import type { ApiResultError } from '../api/client.js';
import {
  useResearchPublishSummary,
  useResearchPublish,
  downloadPublishXlsx,
} from './researchPublishApi.js';

export interface ResearchPublishViewProps {
  /** The research request being published. */
  readonly requestId: number;
  /** Return to the library/history (the "Back to library" success action). */
  readonly onBackToLibrary?: () => void;
}

/**
 * E06-F04-T12 — the h2 is set in the brand serif (Playfair Display), whose square
 * brackets are hairline-thin and read as vertical bars ("|Test|"). Render each
 * bracket in the sans face so a title like "[QA] Fintech" keeps legible brackets
 * while the rest of the heading stays on-brand. Plain text nodes — no HTML.
 */
export function withReadableBrackets(text: string): (string | JSX.Element)[] {
  return text.split(/([[\]])/).map((part, i) =>
    part === '[' || part === ']' ? (
      <span key={i} className="font-sans font-normal">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

/** A labelled count chip in the Publish header. */
function CountChip({
  label,
  value,
  testId,
}: {
  label: string;
  value: number;
  testId: string;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-white px-4 py-3 text-center">
      <div className="text-2xl font-bold text-[var(--color-text-strong)]" data-testid={testId}>
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </div>
    </div>
  );
}

/**
 * A publish option card (icon + title + description). The whole card is a
 * clickable label toggling the embedded checkbox (prototype L1197-1213).
 */
function PublishOptionCard({
  testId,
  checked,
  onToggle,
  icon,
  iconClass,
  title,
  description,
  unavailableReason,
}: {
  testId: string;
  checked: boolean;
  onToggle: () => void;
  icon: string;
  iconClass: string;
  title: string;
  description: string;
  /**
   * Set when the server reported this delivery block as not performable here
   * (no Sheets credentials / no sender mailbox). The checkbox stays ENABLED —
   * an admin may fix the integration between the page load and the click, and a
   * disabled input would silently swallow the explanation — but the amber line
   * says what will happen: the publish still succeeds, that one block just does
   * not deliver, and the success dialog repeats the reason (E07-F06-T06).
   */
  unavailableReason?: string | null;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[var(--color-border)] p-3 hover:border-slate-300">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={onToggle}
        className="accent-[var(--color-accent)]"
      />
      <i className={`fa-solid ${icon} text-lg ${iconClass}`} aria-hidden />
      <div>
        <div className="text-sm font-medium text-[var(--color-text-strong)]">{title}</div>
        <div className="text-xs text-[var(--color-text-muted)]">{description}</div>
        {unavailableReason ? (
          <div className="mt-1 text-xs text-amber-700" data-testid={`${testId}-unavailable`}>
            <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden />
            {unavailableReason}
          </div>
        ) : null}
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
// success dialog
// ---------------------------------------------------------------------------

/**
 * What the publish actually DID, in the user's words.
 *
 * Derived from the options that were SUBMITTED, not from the result counters
 * alone: since E07-F06-T07 made library-only the default, a plain Publish click
 * returns `exported: 0` (the Sheets block never ran) — rendering that counter
 * unconditionally made the default path announce "0 companies exported", which
 * reads as a failed publish. Each block reports only if it was requested.
 */
function outcomeLines(result: PublishResult, options: PublishOptions): string[] {
  const lines: string[] = [];
  if (options.exportToSheets) {
    lines.push(
      // A failed export must not read as "0 companies exported" — that is a
      // silent-loss phrasing for a block that did not run at all (T06).
      result.sheetsError
        ? 'the Google Sheets export did NOT run — see below'
        : `${result.exported} ${result.exported === 1 ? 'company' : 'companies'} exported to Google Sheets` +
            (result.idempotentReuse ? ' (the existing sheet was updated, not duplicated)' : ''),
    );
  }
  if (options.saveToLibrary) {
    lines.push(
      `${result.savedToLibrary} ${result.savedToLibrary === 1 ? 'company' : 'companies'} saved to the reusable library`,
    );
  }
  if (lines.length === 0) {
    lines.push('marked as published — no export or delivery was selected');
  }
  return lines;
}

function PublishSuccessDialog({
  result,
  options,
  sourcersRequested,
  onBackToLibrary,
  onClose,
}: {
  result: PublishResult;
  /** The checkbox state that was actually submitted (drives the outcome copy). */
  options: PublishOptions;
  /** E07-F06-T10 — whether the sourcer email was requested in this publish. */
  sourcersRequested: boolean;
  onBackToLibrary?: () => void;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();

  async function copyLink(): Promise<void> {
    if (!result.sheetUrl) return;
    try {
      await navigator.clipboard.writeText(result.sheetUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Published"
      testId="publish-success"
      maxWidth="lg"
      footer={
        <button
          type="button"
          data-testid="publish-success-back"
          onClick={() => {
            onClose();
            onBackToLibrary?.();
          }}
          className="rounded bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover)]"
        >
          Back to library
        </button>
      }
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <span
          data-testid="publish-success-check"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-2xl text-green-600"
          aria-hidden
        >
          <i className="fa-solid fa-check" />
        </span>
        <ul
          className="flex list-none flex-col gap-1 text-sm text-[var(--color-text)]"
          data-testid="publish-success-summary"
        >
          {outcomeLines(result, options).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>

        {result.sheetUrl ? (
          <div className="flex w-full items-center justify-center gap-2">
            <a
              href={result.sheetUrl}
              target="_blank"
              rel="noopener"
              data-testid="publish-success-sheet-link"
              className="max-w-[20rem] truncate text-sm text-[var(--color-link)] hover:underline"
            >
              {result.sheetUrl}
            </a>
            <button
              type="button"
              data-testid="publish-success-copy"
              onClick={() => void copyLink()}
              className="rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)]"
              aria-label="Copy Sheets link"
            >
              <i className="fa-solid fa-copy mr-1" aria-hidden />
              Copy
            </button>
          </div>
        ) : result.sheetsError ? null : (
          /* Say WHY there is no link, instead of leaving a blank where the user
             expects one (the default flow does not touch Sheets since T07). When
             the export errored the amber block below carries the reason, so this
             muted line would only repeat it. */
          <p
            className="text-xs text-[var(--color-text-muted)]"
            data-testid="publish-success-no-sheet"
          >
            {options.exportToSheets
              ? 'No spreadsheet link came back from Google Sheets.'
              : '“Export to Google Sheets” was not selected — there is no spreadsheet link.'}
          </p>
        )}

        {/* The Sheets block is non-fatal (T06), same as the email block: the rest
            of the publish stands, but say plainly that no spreadsheet was
            created and why. */}
        {result.sheetsError ? (
          <p
            className="w-full rounded-md border-l-4 border-[var(--color-warning)] bg-amber-50 px-3 py-2 text-left text-xs text-amber-800"
            data-testid="publish-success-sheets-error"
          >
            <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden />
            The Google Sheets export did not run: {result.sheetsError}
          </p>
        ) : null}

        <p className="text-xs text-[var(--color-text-muted)]" data-testid="publish-success-meta">
          {result.sentToRecruiter
            ? `Sent to ${result.sentToRecruiter}`
            : options.sendToRecruiter
              ? 'Not sent to the recruiter'
              : 'Recruiter email not selected'}
          {result.linkedProject
            ? ` · linked to ${result.linkedProject}`
            : ' · not linked to a project'}
        </p>

        {/* The email block is non-fatal (T06): the publish succeeded, but say
            plainly that the mail did NOT go out and why. */}
        {result.emailError ? (
          <p
            className="w-full rounded-md border-l-4 border-[var(--color-warning)] bg-amber-50 px-3 py-2 text-left text-xs text-amber-800"
            data-testid="publish-success-email-error"
          >
            <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden />
            The recruiter email was not sent: {result.emailError}
          </p>
        ) : null}

        {/* E07-F06-T10 — the sourcer email (non-fatal, same as the recruiter one). */}
        {sourcersRequested && (result.sentToSourcers ?? []).length > 0 ? (
          <p
            className="text-xs text-[var(--color-text-muted)]"
            data-testid="publish-success-sourcers"
          >
            List sent to {result.sentToSourcers.join(', ')}
            {(result.sourcersCc ?? []).length > 0 ? ` · cc ${result.sourcersCc.join(', ')}` : ''}
          </p>
        ) : null}
        {sourcersRequested && result.sourcersEmailError ? (
          <p
            className="w-full rounded-md border-l-4 border-[var(--color-warning)] bg-amber-50 px-3 py-2 text-left text-xs text-amber-800"
            data-testid="publish-success-sourcers-error"
          >
            <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden />
            The email to the sourcers was not sent: {result.sourcersEmailError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// E07-F06-T10 — sourcer recipients (To) + editable default CC
// ---------------------------------------------------------------------------

/** Loose client-side email check (the server re-validates with zod `.email()`). */
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

function SourcerRecipientsPanel({
  sourcers,
  selected,
  onToggleSourcer,
  cc,
  onRemoveCc,
  onAddCc,
}: {
  sourcers: readonly PublishRecipient[];
  selected: ReadonlySet<string>;
  onToggleSourcer: (email: string) => void;
  cc: readonly string[];
  onRemoveCc: (email: string) => void;
  onAddCc: (email: string) => boolean;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  function addDraft(): void {
    const email = draft.trim();
    if (!EMAIL_RE.test(email)) {
      setDraftError('Enter a valid email address');
      return;
    }
    if (onAddCc(email)) {
      setDraft('');
      setDraftError(null);
    } else {
      setDraftError('Already in the list');
    }
  }

  return (
    <div
      className="ml-8 flex flex-col gap-3 rounded-lg border border-dashed border-[var(--color-border)] p-3"
      data-testid="publish-sourcers-panel"
    >
      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          To — sourcers
        </div>
        {sourcers.length === 0 ? (
          <p className="text-xs text-amber-700" data-testid="publish-sourcers-empty">
            No sourcers in the Address Book — an admin adds them in Admin → Address Book (category
            “Sourcer”).
          </p>
        ) : (
          <ul className="flex flex-col gap-1" data-testid="publish-sourcers-list">
            {sourcers.map((s) => (
              <li key={s.email}>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid="publish-sourcer-option"
                    value={s.email}
                    checked={selected.has(s.email)}
                    onChange={() => onToggleSourcer(s.email)}
                    className="accent-[var(--color-accent)]"
                  />
                  <span className="text-[var(--color-text-strong)]">{s.name}</span>
                  <span className="text-xs text-[var(--color-text-muted)]">{s.email}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
          CC
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="publish-cc-list">
          {cc.length === 0 ? (
            <span className="text-xs text-[var(--color-text-muted)]" data-testid="publish-cc-empty">
              No CC — the default list is set in Admin → Address Book (category “Research Publish
              CC”).
            </span>
          ) : (
            cc.map((email) => (
              <span
                key={email}
                data-testid="publish-cc-chip"
                className="inline-flex items-center gap-1 rounded-full bg-[var(--color-surface-muted)] px-2 py-0.5 text-xs text-[var(--color-text-strong)]"
              >
                {email}
                <button
                  type="button"
                  data-testid="publish-cc-remove"
                  aria-label={`Remove ${email} from CC`}
                  onClick={() => onRemoveCc(email)}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                >
                  <i className="fa-solid fa-xmark" aria-hidden />
                </button>
              </span>
            ))
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <input
            type="email"
            data-testid="publish-cc-input"
            value={draft}
            placeholder="Add CC email"
            aria-label="Add CC email"
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addDraft();
              }
            }}
            className="w-64 rounded border border-[var(--color-border)] px-2 py-1 text-xs"
          />
          <button
            type="button"
            data-testid="publish-cc-add"
            onClick={addDraft}
            className="rounded border border-[var(--color-border)] px-2 py-1 text-xs hover:bg-[var(--color-surface-muted)]"
          >
            Add
          </button>
          {draftError ? (
            <span className="text-xs text-[var(--color-danger)]" data-testid="publish-cc-error">
              {draftError}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// publish screen
// ---------------------------------------------------------------------------

export function ResearchPublishView({
  requestId,
  onBackToLibrary,
}: ResearchPublishViewProps): JSX.Element {
  const toast = useToast();
  const summaryQuery = useResearchPublishSummary(requestId);
  const publish = useResearchPublish(requestId);

  // Three checkboxes — ONLY Save-to-library is checked by default (acceptance
  // #1; E07-F06-T07 rejected the previous all-checked state). The two delivery
  // blocks are real external side effects (a Google Sheet, an email to the
  // recruiter) and must be opted into deliberately.
  const [options, setOptions] = useState<PublishOptions>(DEFAULT_PUBLISH_OPTIONS);
  /**
   * The result AND the options it was produced from — the success copy must
   * describe the blocks the user actually asked for, and `options` may already
   * have been re-toggled by the time the dialog renders.
   */
  const [result, setResult] = useState<{
    res: PublishResult;
    opts: PublishOptions;
    sourcersRequested: boolean;
  } | null>(null);
  const [downloading, setDownloading] = useState(false);

  // E07-F06-T10 — the sourcer email block: opt-in (no side effect by default),
  // To = the ticked sourcers, CC = the Address-Book default until the user edits
  // it (`null` => still the default, so a late-loading summary fills it in).
  const [sendToSourcers, setSendToSourcers] = useState(false);
  const [selectedSourcers, setSelectedSourcers] = useState<ReadonlySet<string>>(new Set());
  const [ccEdited, setCcEdited] = useState<string[] | null>(null);
  const defaultCc = (summaryQuery.data?.defaultCc ?? []).map((r) => r.email);
  const cc = ccEdited ?? defaultCc;

  function toggle(key: keyof PublishOptions): void {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleSourcer(email: string): void {
    setSelectedSourcers((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  function addCc(email: string): boolean {
    if (cc.some((e) => e.toLowerCase() === email.toLowerCase())) return false;
    setCcEdited([...cc, email]);
    return true;
  }

  async function onPublish(): Promise<void> {
    const opts = options;
    const sourcerEmail = sendToSourcers ? { to: [...selectedSourcers], cc } : undefined;
    if (sourcerEmail && sourcerEmail.to.length === 0) {
      toast.error('Select at least one sourcer to send the list to');
      return;
    }
    try {
      const res = await publish.mutateAsync({ options: opts, sourcerEmail });
      setResult({ res, opts, sourcersRequested: sourcerEmail !== undefined });
    } catch (err) {
      // Never swallow the server's reason behind a generic "Publish failed" —
      // that mask is what turned a plain "Sheets is not configured" 409 into an
      // undiagnosable dead end (E07-F06-T06). Same rule as SalaryRateButton.
      const detail = (err as ApiResultError | undefined)?.detail?.message;
      toast.error(detail ? `Publish failed: ${detail}` : 'Publish failed');
    }
  }

  async function onDownloadXlsx(): Promise<void> {
    setDownloading(true);
    try {
      const fileName = await downloadPublishXlsx(requestId);
      toast.success(`Downloaded ${fileName}`);
    } catch (err) {
      const detail = err instanceof Error ? err.message : null;
      toast.error(detail ? `Download failed: ${detail}` : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  if (summaryQuery.isLoading) {
    return (
      <div
        data-testid="publish-loading"
        className="py-8 text-center text-[var(--color-text-muted)]"
      >
        Loading…
      </div>
    );
  }
  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <div data-testid="publish-error" className="py-8 text-center text-[var(--color-danger)]">
        {summaryQuery.error?.detail.message ?? 'Failed to load publish summary'}
      </div>
    );
  }

  const { summary, title, sheetsConfigured, emailConfigured } = summaryQuery.data;
  const sourcers = summaryQuery.data.sourcers ?? [];

  return (
    <div data-testid="research-publish-view" className="flex flex-col gap-6">
      <header>
        <h2
          data-testid="publish-title"
          className="text-lg font-bold text-[var(--color-text-strong)]"
        >
          Publish — {withReadableBrackets(title)}
        </h2>
        {/* gate/auto explainer (acceptance #1). */}
        <p
          data-testid="publish-mode-explainer"
          className="mt-1 text-xs text-[var(--color-text-muted)]"
        >
          {summary.gate
            ? 'Gate mode: only rows you accepted in review are exported.'
            : 'Auto mode: rows auto-accepted by the scorer are exported.'}
        </p>
      </header>

      {/* counts */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="publish-counts">
        <CountChip label="Accepted" value={summary.accepted} testId="publish-count-accepted" />
        <CountChip label="Grade A" value={summary.gradeA} testId="publish-count-a" />
        <CountChip label="Grade B" value={summary.gradeB} testId="publish-count-b" />
        <CountChip label="Grade C" value={summary.gradeC} testId="publish-count-c" />
      </div>

      {/* needs-human warning (acceptance #2) */}
      {summary.needsHumanFlagged > 0 ? (
        <div
          data-testid="publish-needs-human-warning"
          className="rounded-md border-l-4 border-[var(--color-warning)] bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden />
          {summary.needsHumanFlagged} accepted{' '}
          {summary.needsHumanFlagged === 1 ? 'company is' : 'companies are'} still flagged{' '}
          <b>needs human review</b> — verify the evidence before publishing.
        </div>
      ) : null}

      {/* E07-F04-T11 — unchecked accepted rows, counted separately (never blocks). */}
      {(summary.notCheckedFlagged ?? 0) > 0 ? (
        <div
          data-testid="publish-not-checked-warning"
          className="rounded-md border-l-4 border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700"
        >
          <i className="fa-solid fa-circle-minus mr-1" aria-hidden />
          {summary.notCheckedFlagged} accepted{' '}
          {summary.notCheckedFlagged === 1 ? 'company has' : 'companies have'}{' '}
          <b>not been checked</b> — no AI check or Verify has run on{' '}
          {summary.notCheckedFlagged === 1 ? 'it' : 'them'} yet.
        </div>
      ) : null}

      {/* option cards (prototype L1197-1213) — icon + title + description, the
          whole card is a clickable label toggling the checkbox. */}
      <fieldset className="flex flex-col gap-3" data-testid="publish-options">
        <legend className="mb-1 text-sm font-medium text-[var(--color-text-strong)]">
          Publish actions
        </legend>
        <PublishOptionCard
          testId="publish-opt-sheets"
          checked={options.exportToSheets}
          onToggle={() => toggle('exportToSheets')}
          icon="fa-table-cells"
          iconClass="text-green-600"
          title="Export to Google Sheets"
          description="EER table: Grade · Comments · Company · Website · … · Country · HQ Location · Global Company Size"
          unavailableReason={
            sheetsConfigured
              ? null
              : 'Google Sheets is not connected on this environment — an admin must add the service-account credentials; the publish will still go through, but no spreadsheet will be created.'
          }
        />
        <PublishOptionCard
          testId="publish-opt-recruiter"
          checked={options.sendToRecruiter}
          onToggle={() => toggle('sendToRecruiter')}
          icon="fa-paper-plane"
          iconClass="text-blue-600"
          title="Send the link to the recruiter"
          description="Emails the recruiter the Sheets link, linked to the project"
          unavailableReason={
            emailConfigured
              ? null
              : 'No mailbox is connected for your account — connect Outlook first, otherwise the publish succeeds but no email goes out.'
          }
        />
        <PublishOptionCard
          testId="publish-opt-sourcers"
          checked={sendToSourcers}
          onToggle={() => setSendToSourcers((v) => !v)}
          icon="fa-users"
          iconClass="text-indigo-600"
          title="Send the list to sourcers"
          description="Emails the selected sourcers the list (.xlsx attached + Sheets link), CC the sourcing teamlead and Himanshu"
          unavailableReason={
            emailConfigured
              ? null
              : 'No mailbox is connected for your account — connect Outlook first, otherwise the publish succeeds but no email goes out.'
          }
        />
        {sendToSourcers ? (
          <SourcerRecipientsPanel
            sourcers={sourcers}
            selected={selectedSourcers}
            onToggleSourcer={toggleSourcer}
            cc={cc}
            onRemoveCc={(email) => setCcEdited(cc.filter((e) => e !== email))}
            onAddCc={addCc}
          />
        ) : null}
        <PublishOptionCard
          testId="publish-opt-library"
          checked={options.saveToLibrary}
          onToggle={() => toggle('saveToLibrary')}
          icon="fa-book-bookmark"
          iconClass="text-slate-500"
          title="Save companies to the reusable library"
          description="Cache enrichment + verification for future searches (default on)"
        />
      </fieldset>

      <div className="flex justify-end gap-3">
        {/* E07-F06-T08 — the same EER table as a file, with or without Sheets. */}
        <button
          type="button"
          data-testid="publish-download-xlsx"
          onClick={() => void onDownloadXlsx()}
          disabled={downloading || summary.accepted === 0}
          title={
            summary.accepted === 0
              ? 'No accepted companies to download yet'
              : 'Download the accepted companies as an .xlsx file (EER columns)'
          }
          className="rounded-md border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium text-[var(--color-text-strong)] hover:bg-[var(--color-surface-muted)] disabled:opacity-60"
        >
          <i className="fa-solid fa-file-excel mr-1 text-green-600" aria-hidden />
          {downloading ? 'Preparing…' : 'Download .xlsx'}
        </button>
        <button
          type="button"
          data-testid="publish-submit"
          onClick={() => void onPublish()}
          disabled={publish.isPending}
          className="rounded-md bg-[var(--color-primary)] px-5 py-2 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
        >
          {publish.isPending ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      {result ? (
        <PublishSuccessDialog
          result={result.res}
          options={result.opts}
          sourcersRequested={result.sourcersRequested}
          onBackToLibrary={onBackToLibrary}
          onClose={() => setResult(null)}
        />
      ) : null}
    </div>
  );
}

export default ResearchPublishView;
